import type { HTTPError } from "ky";
import type { ResponseType } from "@/types/general";

type ApiErrorBody = Partial<ResponseType<unknown>>;

const isHttpError = (error: unknown): error is HTTPError =>
	!!error && (error as HTTPError).name === "HTTPError";

/** Parsed body of a failed request, or null when it was not an API error. */
export const getApiErrorData = async (
	error: unknown,
): Promise<ApiErrorBody | null> => {
	if (!isHttpError(error)) return null;
	try {
		return (await error.response.clone().json()) as ApiErrorBody;
	} catch {
		return null;
	}
};

export const getApiErrorMessage = async (
	error: unknown,
	fallback = "Terjadi kesalahan. Silakan coba lagi.",
): Promise<string> => {
	const body = await getApiErrorData(error);
	return body?.message || (error instanceof Error ? error.message : fallback);
};

/**
 * Field-level validation errors, shaped for react-hook-form's setError:
 * `Object.entries(await getFieldErrors(e)).forEach(([f, m]) => form.setError(f, { message: m }))`
 */
export const getFieldErrors = async (
	error: unknown,
): Promise<Record<string, string>> => {
	const body = await getApiErrorData(error);
	const fields: Record<string, string> = {};
	for (const e of body?.errors ?? []) {
		if (e.field && e.messages?.length) fields[e.field] = e.messages[0];
	}
	return fields;
};
